import { FC, useCallback, useEffect, useState } from 'react';
import I18nHelpers from 'i18n/I18nHelpers';
import { useTranslation } from 'hooks';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import api from 'api/getStarted';
import Icon from 'components/ui/icon';
import CopyToClipboard from 'components/ui/copyToClipboard';
import { Skeleton } from 'components/ui/skeleton';
import CustomSelect from 'components/ui/customSelect';

export const CredentialsCard: FC = () => {
  const getTranslate = useTranslation();
  const [isLoading, setLoading] = useState(true);
  const [projectOptions, setProjectOptions] = useState([]);
  const [selectedProject, setSelectedProject] = useState<{
    value: string;
    label: string;
    secretKey: string;
    title: string;
  } | null>(null);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const wl = useSelector((state: RootState) => state.settings.wl);
  const welcomeMessage = I18nHelpers.parseString(
    getTranslate('getStarted.welcome.label')
  );

  useEffect(() => {
    getDemoProject();
  }, []);

  const toggleSecretKey = useCallback(() => {
    setShowSecretKey((old) => !old);
  }, []);

  const getDemoProject = async () => {
    setLoading(true);

    try {
      const projects = await api.getDemoProject();
      const options = projects.map(({ projectId, title, secretKey }) => ({
        value: projectId,
        label: title,
        secretKey,
      }));

      setProjectOptions(options);
      setSelectedProject(options[0]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='text-text-inverse bg-brand-main card max-desktop-s:flex-1 max-tablet-l:w-full relative w-[390px] flex-shrink-0 self-stretch p-5'>
      <h1 className='font-heading text-heading-l mb-5'>
        <b>{welcomeMessage.shift()}</b> {welcomeMessage} {wl.toUpperCase()}
      </h1>
      <p className='text-m-medium mb-2'>
        {getTranslate('getStarted.welcome.text')}
      </p>
      <div>
        <CustomSelect
          label='myteam.bunchEdit.modal.selectProject.title'
          isLoading={isLoading}
          value={selectedProject}
          options={projectOptions}
          customClass='ui-select_large'
          onChange={setSelectedProject}
        />
        <div className='text-l-regular border-border-base relative flex w-full justify-between gap-2 border-b py-4'>
          <div className='w-[100px]'>
            {getTranslate('getStarted.projectID.label')}
          </div>
          {isLoading ? (
            <Skeleton isLight height={20} />
          ) : (
            <>
              <div className='flex-1 overflow-hidden text-ellipsis whitespace-nowrap'>
                {selectedProject!.value}
              </div>
              <CopyToClipboard
                text={selectedProject!.value}
                customClass='text-text-inverse'
              />
            </>
          )}
        </div>
        <div className='text-l-regular relative flex w-full justify-between gap-2 py-4'>
          <div className='w-[100px]'>
            {getTranslate('getStarted.secretKey.label')}
          </div>
          {isLoading ? (
            <Skeleton isLight height={20} />
          ) : (
            <>
              <div className='flex-1 overflow-hidden break-words'>
                <div
                  className={`transition-all duration-200 ease-in-out ${
                    !showSecretKey
                      ? 'line-clamp-1 max-h-[20px]'
                      : 'max-h-[200px]'
                  }`}>
                  {selectedProject?.secretKey}
                </div>
              </div>

              <div className='flex justify-between gap-4'>
                <div>
                  <Icon
                    size={14}
                    className='cursor-pointer'
                    name={showSecretKey ? 'im-Close' : 'im-Arrow-down'}
                    onClick={toggleSecretKey}
                  />
                </div>
                <CopyToClipboard
                  text={selectedProject!.secretKey}
                  customClass='text-text-inverse static'
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
